import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import jwtInterceoptor from "../../../api/jwtInterceptor";
import { DELETE_API_URLS } from "../../../utils/constant";
import { FormErrorPopup, ConfirmPopup } from "../../parts/popups";
import Button from "../button";
import styles from "./style.module.scss";

const PropanePromotionCard = (props) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const navigate = useNavigate();

  // Debug logging
  console.log("PropanePromotionCard props:", props);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if promotion is active
  // const isPromotionActive = () => {
  //   const now = new Date();
  //   const effectiveDate = new Date(props.effective_date);
  //   const expirationDate = new Date(props.expiration_date);
  //   return now >= effectiveDate && now <= expirationDate;
  // };

  // Get promotion status
  const getPromotionStatus = () => {
    const now = new Date();
    const effectiveDate = new Date(props.effective_date);
    const expirationDate = new Date(props.expiration_date);
    
    if (now < effectiveDate) return { status: 'upcoming', label: 'Upcoming', color: '#ffc107' };
    if (now > expirationDate) return { status: 'expired', label: 'Expired', color: '#dc3545' };
    return { status: 'active', label: 'Active', color: '#28a745' };
  };

  const editPropanePromotionHandler = (data) => {
    navigate("/dashboard/propane-promotions/edit", { state: data });
  };

  const deletePropanePromotionHandler = (id) => {
    setIsDeleting(true);
    jwtInterceoptor
      .delete(DELETE_API_URLS.DELETE_PROPANE_PROMOTION + `${id}/`)
      .then((response) => {
        setIsDeleting(false);
        setShowConfirmDelete(false);
        console.log(response.data);
        if (props.onDelete) {
          props.onDelete();
        }
      })
      .catch((error) => {
        setIsDeleting(false);
        setShowConfirmDelete(false);
        setError(error.message);
        console.log("Error", error);
      });
  };

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    deletePropanePromotionHandler(props.id);
  };

  const handleCancelDelete = () => {
    setShowConfirmDelete(false);
  };

  const statusInfo = getPromotionStatus();

  return (
    <>
      <div className={styles.propanePromotionCard}>
        {/* Status Badge */}
        <div className={styles.statusBadge} style={{ backgroundColor: statusInfo.color }}>
          {statusInfo.label}
        </div>

        {/* Promotion Image */}
        <div className={styles.promotionImage}>
          <img 
            src={props.image || props.image_url} 
            alt={props.name || "Propane Promotion"} 
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik04MCAxMDBDODAgODkuNTQ0NyA4OC4wMDAxIDgxIDEwMCA4MUMxMTEuOTU2IDgxIDEyMCA4OS41NDQ3IDEyMCAxMDBDMTIwIDExMC40NTUgMTExLjk1NiAxMTkgMTAwIDExOUM4OC4wMDAxIDExOSA4MCAxMTAuNDU1IDgwIDEwMFoiIGZpbGw9IiNDQ0NDQ0MiLz4KPHBhdGggZD0iTTEwMCAxMjBDMTEwLjQ1NyAxMjAgMTE5IDEyOC41NDMgMTE5IDEzOEMxMTkgMTQ3LjQ1NyAxMTAuNDU3IDE1NiAxMDAgMTU2Qzg5LjU0MzQgMTU2IDgxIDE0Ny40NTcgODEgMTM4QzgxIDEyOC41NDMgODkuNTQzNCAxMjAgMTAwIDEyMFoiIGZpbGw9IiNDQ0NDQ0MiLz4KPC9zdmc+';
              e.target.onerror = null;
            }}
          />
        </div>

        {/* Promotion Content */}
        <div className={styles.promotionContent}>
          {/* Title and Status */}
          <div className={styles.promotionHeader}>
            <h2>{props.name || props.title}</h2>
            <div className={styles.statusIndicator} style={{ backgroundColor: statusInfo.color }}></div>
          </div>

          {/* Description */}
          {props.description && (
            <div className={styles.promotionDescription}>
              <p>{props.description}</p>
            </div>
          )}

          {/* Promotion Details */}
          <div className={styles.promotionInfo}>
            {/* Date Information */}
            <div className={styles.dateSection}>
              <h4>Promotion Period</h4>
              <div className={styles.dateGrid}>
                <div className={styles.dateItem}>
                  <div className={styles.dateIcon}>📅</div>
                  <div className={styles.dateContent}>
                    <span className={styles.dateLabel}>Effective Date</span>
                    <span className={styles.dateValue}>
                      {formatDate(props.effective_date || props.startDate)}
                    </span>
                  </div>
                </div>
                <div className={styles.dateItem}>
                  <div className={styles.dateIcon}>⏰</div>
                  <div className={styles.dateContent}>
                    <span className={styles.dateLabel}>Expiration Date</span>
                    <span className={styles.dateValue}>
                      {formatDate(props.expiration_date || props.endDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hyperlink */}
            {(props.hyperlink || props.link) && (
              <div className={styles.hyperlinkSection}>
                <h4>External Link</h4>
                <div className={styles.linkContainer}>
                  <div className={styles.linkIcon}>🔗</div>
                  <a 
                    href={props.hyperlink || props.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.hyperlink}
                  >
                    {(props.hyperlink || props.link).length > 50 
                      ? (props.hyperlink || props.link).substring(0, 50) + '...' 
                      : (props.hyperlink || props.link)
                    }
                  </a>
                </div>
              </div>
            )}

            {/* Status Info */}
            {/* <div className={styles.statusInfo}>
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Current Status</span>
                <span className={styles.statusValue} style={{ color: statusInfo.color }}>
                  {statusInfo.label}
                </span>
              </div>
            </div> */}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.btnContainer}>
          <Button
            label="Edit"
            secondary
            onClick={() => editPropanePromotionHandler(props.data || props)}
          />
          <Button
            label={isDeleting ? "Deleting..." : "Delete"}
            primary
            onClick={handleDeleteClick}
            disabled={isDeleting}
          />
        </div>
      </div>
      
      {/* Confirmation Popup */}
      <ConfirmPopup
        show={showConfirmDelete}
        title="Delete Propane Promotion"
        message="Are you sure you want to delete this propane promotion? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isDeleting}
      />
      
      {error ? (
        <FormErrorPopup
          error={error}
          close={() => setError(false)}
          errorText="Oops! An error occurred"
        />
      ) : null}
    </>
  );
};

// Demo component for development/testing
export const PropanePromotionCardDemo = () => {
  const demoData = {
    id: 1,
    name: "Summer Propane Savings",
    description: "Get amazing discounts on propane refills during the summer months. Perfect for your BBQ and outdoor activities. Limited time offer with great value for money.",
    effective_date: "2024-06-01",
    expiration_date: "2024-08-31",
    link: "https://example.com/summer-propane-savings",
    image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop"
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '400px', margin: '0 auto' }}>
      <PropanePromotionCard {...demoData} />
    </div>
  );
};

export default PropanePromotionCard;
